import type { ConnectLibraryAnswer } from "@/lib/api/connect-library";
import { cn } from "@/lib/utils";
import { colorsV2 } from "@somethings-ui/core";
import type {
  ConnectLevel,
  ConnectQuestion,
} from "@somethings-ui/core/connect-questions";
import { Shuffle } from "lucide-react";
import { ConnectRowRecorder } from "./connect-row-recorder";
import { MicSelector } from "./mic-selector";

const DIVIDER = "#f2f0ec";
const BUTTON_BORDER = "#d6d3cd";

interface ConnectMomentComposeStepProps {
  mentorshipId: string;
  questions: Record<ConnectLevel, ConnectQuestion>;
  libraryByQuestionId: Record<string, ConnectLibraryAnswer>;
  recordingState: Record<ConnectLevel, boolean>;
  isAnyRowRecording: boolean;
  selectedMicId: string | null;
  onSelectMic: (deviceId: string | null) => void;
  onShuffleAll: () => void;
  onShuffleOne: (level: ConnectLevel) => void;
  onRecordingStateChange: (level: ConnectLevel) => (recording: boolean) => void;
  onLocalBlobChange: (level: ConnectLevel) => (hasBlob: boolean) => void;
  onBack: () => void;
  onSend: () => void;
  canSend: boolean;
  isSending: boolean;
  sendError: string | null;
}

export function ConnectMomentComposeStep({
  mentorshipId,
  questions,
  libraryByQuestionId,
  recordingState,
  isAnyRowRecording,
  selectedMicId,
  onSelectMic,
  onShuffleAll,
  onShuffleOne,
  onRecordingStateChange,
  onLocalBlobChange,
  onBack,
  onSend,
  canSend,
  isSending,
  sendError,
}: ConnectMomentComposeStepProps) {
  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 pb-2 pt-3">
        <div className="mb-2 flex items-center justify-end gap-1">
          <MicSelector
            selectedDeviceId={selectedMicId}
            onSelect={onSelectMic}
            disabled={isAnyRowRecording}
          />
          <button
            type="button"
            onClick={onShuffleAll}
            disabled={isAnyRowRecording}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ color: colorsV2.text.secondary }}
          >
            <Shuffle className="h-3.5 w-3.5" />
            New cards
          </button>
        </div>

        <div className="flex flex-col gap-1">
          {([1, 2, 3] as const).map((level) => (
            <ConnectRowRecorder
              key={`${level}:${questions[level].id}`}
              mentorshipId={mentorshipId}
              level={level}
              questionText={questions[level].text}
              questionId={questions[level].id}
              libraryAnswer={libraryByQuestionId[questions[level].id] ?? null}
              isAnotherRowRecording={
                isAnyRowRecording && !recordingState[level]
              }
              micDeviceId={selectedMicId}
              onRecordingStateChange={onRecordingStateChange(level)}
              onLocalBlobChange={onLocalBlobChange(level)}
              onShuffleQuestion={() => onShuffleOne(level)}
              shuffleDisabled={isAnyRowRecording}
            />
          ))}
        </div>
      </div>

      <div
        className="flex flex-col gap-3 border-t px-6 pb-5 pt-4"
        style={{ borderColor: DIVIDER }}
      >
        {sendError && (
          <p
            role="alert"
            className="text-sm"
            style={{ color: colorsV2.states.error }}
          >
            {sendError}
          </p>
        )}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isSending || isAnyRowRecording}
            className={cn(
              "flex-1 rounded-full border bg-white py-3 text-sm font-bold",
              "hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-zinc-300",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
            style={{
              borderColor: BUTTON_BORDER,
              color: colorsV2.text.primary,
            }}
          >
            Back
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend || isSending}
            className={cn(
              "flex-1 rounded-full py-3 text-sm font-bold text-white",
              "focus:outline-none focus:ring-2 focus:ring-zinc-700",
              canSend && !isSending
                ? "bg-black hover:bg-zinc-900"
                : "cursor-not-allowed bg-[#d6d3cd]",
            )}
          >
            {isSending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </>
  );
}
